/**
 * useEnrollment Hook
 */

import { useState, useCallback } from 'react';
import { enrollInCourse, cancelEnrollment } from '../../services/coursesService';
import type { EnrollmentStatus } from '../../types/courses.types';

interface EnrollResult {
  success: boolean;
  enrollmentId?: number;
  status?: EnrollmentStatus;
  requiresApproval?: boolean;
  error?: string;
}

interface CancelResult {
  success: boolean;
  newStatus?: EnrollmentStatus;
  error?: string;
}

interface UseEnrollmentResult {
  isEnrolling: boolean;
  isCancelling: boolean;
  enroll: (slug: string) => Promise<EnrollResult>;
  cancel: (enrollmentId: number) => Promise<CancelResult>;
}

export function useEnrollment(employeeId: number | null): UseEnrollmentResult {
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const enroll = useCallback(
    async (slug: string): Promise<EnrollResult> => {
      if (!employeeId) return { success: false, error: 'Manglende parametre' };
      setIsEnrolling(true);
      try {
        const result = await enrollInCourse(slug);
        return {
          success: true,
          enrollmentId: result.enrollmentId,
          status: result.status,
          requiresApproval: result.requiresApproval,
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Fejl ved tilmelding',
        };
      } finally {
        setIsEnrolling(false);
      }
    },
    [employeeId]
  );

  const cancel = useCallback(
    async (enrollmentId: number): Promise<CancelResult> => {
      if (!employeeId) return { success: false, error: 'Manglende parametre' };
      setIsCancelling(true);
      try {
        const result = await cancelEnrollment(enrollmentId);
        return { success: true, newStatus: result.newStatus };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Fejl ved afmelding',
        };
      } finally {
        setIsCancelling(false);
      }
    },
    [employeeId]
  );

  return { isEnrolling, isCancelling, enroll, cancel };
}
